import { GenAiAssistantFeedbackRating, FeedbackTargetNotFoundError } from '@/src/api/GenAiAPI';
import { useApi } from '@/src/store/api.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, FormField, Icon, Modal, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FeedbackButtons.module.scss';
import a11yStyles from '@/src/styles/a11y.module.scss';

const MAX_COMMENT_LENGTH = 2000;

/**
 * Persistent feedback state for a single assistant response. Lives in a parent
 * that stays mounted (the chat container) so the user's selection survives the
 * chat window being closed and reopened.
 */
export interface FeedbackState {
  /** The currently active thumb, or null if none/cleared. */
  rating: GenAiAssistantFeedbackRating | null;
  /** The last rating submitted to the backend (persists after clearing). */
  lastRating: GenAiAssistantFeedbackRating | null;
  /** Whether a comment has been submitted, to show the thanks confirmation. */
  commentSubmitted: boolean;
}

export const emptyFeedbackState: FeedbackState = {
  rating: null,
  lastRating: null,
  commentSubmitted: false,
};

export interface FeedbackButtonsProps {
  /** The id of the assistant query this feedback applies to. */
  queryId: string;
  /** Persistent feedback state for this response, owned by the parent. */
  feedback?: FeedbackState;
  /** Called when the persistent feedback state changes, so the parent can store it. */
  onFeedbackChange?: (next: FeedbackState) => void;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  queryId,
  feedback = emptyFeedbackState,
  onFeedbackChange,
}) => {
  const { t } = useTranslation();
  const { genAiAPI } = useApi();
  // Internal mirror of the persistent feedback state so the component works
  // standalone, while staying in sync with the parent-owned `feedback` prop
  // (which is what survives the chat window closing/reopening).
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(feedback);
  useEffect(() => {
    setFeedbackState(feedback);
  }, [feedback]);
  const { rating, lastRating, commentSubmitted } = feedbackState;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  const updateFeedback = (next: FeedbackState) => {
    setFeedbackState(next);
    onFeedbackChange?.(next);
  };

  const announce = (message: string) => {
    setLiveMessage(message);
  };

  const handleError = (error: unknown) => {
    if (error instanceof FeedbackTargetNotFoundError) {
      announce(t(i18nKeys.aiAssistant.feedback.notAvailable));
    } else {
      announce(t(i18nKeys.aiAssistant.feedback.error));
    }
  };

  const submitFeedback = async (rated: GenAiAssistantFeedbackRating, commentText?: string) => {
    setIsSubmitting(true);
    try {
      await genAiAPI.submitAssistantFeedback({ queryId, feedback: rated, comment: commentText });
      const hasComment = !!commentText && !!commentText.trim();
      updateFeedback({
        rating: rated,
        lastRating: rated,
        commentSubmitted: commentSubmitted || hasComment,
      });
      if (hasComment) {
        announce(t(i18nKeys.aiAssistant.feedback.thanks));
      } else {
        announce(t(i18nKeys.aiAssistant.feedback.ratingRecorded));
      }
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumb = async (rated: GenAiAssistantFeedbackRating) => {
    if (isSubmitting) {
      return;
    }
    // Clicking the already-active thumb clears the visible selection locally
    // without sending another request. The additional-feedback flow stays
    // available via the previously submitted rating.
    if (rating === rated) {
      updateFeedback({ ...feedbackState, rating: null });
      return;
    }
    await submitFeedback(rated);
  };

  const openCommentModal = () => {
    setComment('');
    setCommentError(null);
    setIsModalOpen(true);
  };

  const closeCommentModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsModalOpen(false);
  };

  const handleCommentSubmit = async () => {
    if (!lastRating) {
      return;
    }
    if (comment.length > MAX_COMMENT_LENGTH) {
      setCommentError(t(i18nKeys.aiAssistant.feedback.tooLong));
      return;
    }
    const trimmed = comment.trim();
    const succeeded = await submitFeedback(lastRating, trimmed || undefined);
    if (succeeded) {
      setIsModalOpen(false);
    }
  };

  return (
    <div className={styles.feedback}>
      <div className={styles.buttons} role="group" aria-label={t(i18nKeys.aiAssistant.feedback.groupLabel)}>
        <button
          type="button"
          className={`${styles.thumb} ${rating === 'positive' ? styles.active : ''}`}
          aria-label={t(i18nKeys.aiAssistant.feedback.thumbsUp)}
          aria-pressed={rating === 'positive'}
          disabled={isSubmitting}
          onClick={() => handleThumb('positive')}
          data-testid="feedback-thumbs-up"
        >
          <Icon name={rating === 'positive' ? 'thumbs-up-filled' : 'thumbs-up'} />
        </button>
        <button
          type="button"
          className={`${styles.thumb} ${rating === 'negative' ? styles.active : ''}`}
          aria-label={t(i18nKeys.aiAssistant.feedback.thumbsDown)}
          aria-pressed={rating === 'negative'}
          disabled={isSubmitting}
          onClick={() => handleThumb('negative')}
          data-testid="feedback-thumbs-down"
        >
          <Icon name={rating === 'negative' ? 'thumbs-down-filled' : 'thumbs-down'} />
        </button>
        {lastRating && (
          <Button
            variant="inline-link"
            disabled={isSubmitting}
            onClick={openCommentModal}
            data-testid="feedback-additional"
          >
            {t(i18nKeys.aiAssistant.feedback.submitAdditional)}
          </Button>
        )}
        {commentSubmitted && (
          <span className={styles.thanks} data-testid="feedback-thanks">
            {t(i18nKeys.aiAssistant.feedback.thanks)}
          </span>
        )}
      </div>

      <div role="status" aria-live="polite" className={a11yStyles.srOnly} data-testid="feedback-live-region">
        {liveMessage}
      </div>

      <Modal
        visible={isModalOpen}
        onDismiss={closeCommentModal}
        header={t(i18nKeys.aiAssistant.feedback.modalTitle)}
        closeAriaLabel={t(i18nKeys.aiAssistant.feedback.cancel)}
        data-testid="feedback-comment-modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={isSubmitting} onClick={closeCommentModal}>
                {t(i18nKeys.aiAssistant.feedback.cancel)}
              </Button>
              <Button
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleCommentSubmit}
                data-testid="feedback-comment-submit"
              >
                {t(i18nKeys.aiAssistant.feedback.submit)}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label={t(i18nKeys.aiAssistant.feedback.commentLabel)}
          description={t(i18nKeys.general.avoidPIIWarning)}
          errorText={commentError ?? undefined}
          constraintText={`${comment.length}/${MAX_COMMENT_LENGTH}`}
        >
          <Textarea
            value={comment}
            onChange={({ detail }) => {
              setComment(detail.value);
              if (commentError && detail.value.length <= MAX_COMMENT_LENGTH) {
                setCommentError(null);
              }
            }}
            placeholder={t(i18nKeys.aiAssistant.feedback.commentPlaceholder)}
            ariaLabel={t(i18nKeys.aiAssistant.feedback.commentLabel)}
            disabled={isSubmitting}
            rows={4}
            data-testid="feedback-comment-input"
          />
        </FormField>
      </Modal>
    </div>
  );
};

export default FeedbackButtons;
